import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { SYSTEM_MESSAGE_PREFIX } from "lib/emitter";
import { localizeSystemTextFragment } from "lib/systemMessageText";
import { OptimisticMessage } from "types";

/**
 * Type guard: checks if a message is a locally-created optimistic placeholder
 * that has NOT yet been resolved with the server response. Once resolved we
 * preserve `_optimisticId` on the real message so Virtuoso's React key stays
 * stable (no remount → no scroll jump), and discriminate on `_status` which
 * is only set while the placeholder is still pending ("sending" / "failed").
 */
export function isOptimisticMessage(m: Message): m is OptimisticMessage {
  return "_optimisticId" in m && "_status" in m;
}

export interface MessageMetadata {
  type?: string;
  replyToMessageId?: string;
  replyToText?: string;
  replyToSenderId?: string;
  replyToSenderName?: string;
  forwardedFromId?: string;
  forwardedFromName?: string;
  voiceMessage?: boolean;
  voiceDuration?: number;
  voiceTranscription?: string;
  videoMessage?: boolean;
  videoDuration?: number;
  deleted?: boolean;
  deletedForAll?: boolean;
  threadParentId?: string;
}

/**
 * Extract metadata from message.details (set by GraphQL details resolver).
 * The server parses __META__: prefix and puts it into details with __meta: true flag.
 */
export function getMessageMetadata(message: Message): MessageMetadata | null {
  const details = (message as any).details;
  if (!details || !details.__meta) return null;
  return details as MessageMetadata;
}

/**
 * Strip __META__ prefix from raw text (for cases where raw text is available).
 */
export function stripMetaPrefix(text: string | null | undefined): string {
  if (!text) return "";
  const META_PREFIX = "__META__:";
  if (!text.startsWith(META_PREFIX)) return text;
  const newlineIdx = text.indexOf("\n");
  return newlineIdx >= 0 ? text.substring(newlineIdx + 1) : "";
}

/**
 * Build a clean, human-readable one-line preview of a message's raw text for OS
 * notifications — stripping internal markers (__SYSTEM__:, __META__:{json},
 * [[...]] link markers, raw URLs), labelling voice / round-video, and removing
 * markdown/mention syntax. Used by the desktop/web OS-notification fallback in
 * PushManager so it never shows raw markers. `t` is the i18n translator.
 *
 * Parity with the backend `PushMessageTextFormatter` (FCM/APNs push body) is
 * approximate: media labels (voice/video/attachment) ARE localised here, but
 * known __SYSTEM__ templates are returned as cleaned ENGLISH text — the backend
 * additionally localises them, whereas this fallback only strips their markers.
 * That gap affects only the OS-fallback path (tab hidden) on non-EN locales and
 * is purely cosmetic; the in-app rendering (MessagesListView) localises fully.
 */
export function cleanMessagePreview(
  rawText: string | null | undefined,
  t: (key: string, vars?: Record<string, string | number>) => string
): string {
  const MAX = 120;
  const truncate = (s: string) => {
    if (s.length <= MAX) return s;
    let cut = MAX - 3;
    // Avoid splitting a surrogate pair (emoji) → no lone half-char before "...".
    const code = s.charCodeAt(cut - 1);
    if (cut > 0 && code >= 0xd800 && code <= 0xdbff) cut--;
    return s.substring(0, cut) + "...";
  };
  if (!rawText || !rawText.trim()) return t("Sent an attachment");

  let text = rawText;
  const META_PREFIX = "__META__:";
  if (text.startsWith(META_PREFIX)) {
    const nl = text.indexOf("\n");
    const json = nl >= 0 ? text.substring(META_PREFIX.length, nl) : text.substring(META_PREFIX.length);
    const caption = nl >= 0 ? text.substring(nl + 1) : "";
    try {
      const meta = JSON.parse(json);
      if (meta?.voiceMessage) return `🎤 ${t("Voice message")}`;
      if (meta?.videoMessage) return `🎥 ${t("Video message")}`;
    } catch {
      /* not valid metadata JSON — treat remainder as a caption */
    }
    if (!caption.trim()) return t("Sent an attachment");
    text = caption;
  }

  const SYSTEM_PREFIX = SYSTEM_MESSAGE_PREFIX;
  if (text.startsWith(SYSTEM_PREFIX)) {
    const body = text
      .substring(SYSTEM_PREFIX.length)
      .replace(/\[\[[^\]]*\]\]/g, "") // [[insights:uuid]] link markers
      .replace(/https?:\/\/\S+/g, "") // raw recording/transcript URLs
      .replace(/\s{2,}/g, " ")
      .trim()
      .replace(/:+\s*$/, "") // dangling colon left after URL removal
      .trim();
    // Known system templates ("X added Y to the group", "Meeting insights
    // ready (…)", "Video call ended (…)") are stored in English — localise.
    return truncate(localizeSystemTextFragment(body, t));
  }

  // Plain user text (or an attachment caption) — strip markdown/mention syntax
  // to match the chat-list preview (MessageThreadItem.lastMessageText). Emphasis
  // stripping is word-boundary-gated (delimiter at a token edge, not wrapping
  // another delimiter), so intra-word `_`/`*` in filenames, snake_case, URLs and
  // math survive (report_2024_final, my_page_name, 2*x) — only real emphasis is
  // removed. No lookbehind (CRA4/older-engine safe).
  text = text
    // [[insights:uuid]]-style internal markers can also appear WITHOUT a
    // __SYSTEM__ prefix — never show them raw.
    .replace(/\[\[[^\]]*\]\]/g, "")
    .replace(/```[\s\S]*?```/g, "[code]")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/(^|[^\w*])\*\*([^*]+?)\*\*(?=[^\w*]|$)/g, "$1$2")
    .replace(/(^|[^\w_])__([^_]+?)__(?=[^\w_]|$)/g, "$1$2")
    .replace(/(^|[^\w*])\*([^*]+?)\*(?=[^\w*]|$)/g, "$1$2")
    .replace(/(^|[^\w_])_([^_]+?)_(?=[^\w_]|$)/g, "$1$2")
    .replace(/~~(.+?)~~/g, "$1")
    .replace(/@\[(\d+)]/g, "@user");
  return truncate(text);
}
